import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useToast } from '../components/Toast';
import TelegramNotify, { sendActivityNotification } from '../components/TelegramNotify';
import { 
  formatUSDT, formatNumber, generateUserId, generateReferralLink, 
  copyToClipboard, parseUSDT, getRandomAvatar, getActivityLabel, timeAgo,
  formatSmart
} from '../utils/helpers';
import Modal from '../components/Modal';
import Loader from '../components/Loader';
import './Home.css';

const Home = () => {
  const {
    account,
    planContract,
    storageContract,
    usdtContract,
    usdtBalance,
    userDetails,
    planConfig,
    refreshUserData,
    provider
  } = useWeb3();
  const toast = useToast();

  const [contractBalance, setContractBalance] = useState('0');
  const [capitalPool, setCapitalPool] = useState('0');
  const [stats, setStats] = useState({
    totalEarned: '0',
    teamCount: '0',
    directCount: '0',
    teamBusiness: '0'
  });
  const [income, setIncome] = useState({
    sponsorIncome: '0',
    levelIncome: '0',
    profitIncome: '0',
    autoReferralIncome: '0',
    totalEarned: '0'
  });
  const [claimable, setClaimable] = useState({
    sponsor: '0',
    level: '0',
    autoRef: '0',
    total: '0'
  });
  const [activities, setActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('50');
  const [purchaseMode, setPurchaseMode] = useState('auto'); // 'auto' | 'normal'
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRepurchasing, setIsRepurchasing] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [repurchaseAmount, setRepurchaseAmount] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const ACTIVITIES_PER_PAGE = 10;

  const userId = generateUserId(account);
  const uplineId = userDetails?.sponsor ? generateUserId(userDetails.sponsor) : 'AD000000';
  const referralLink = generateReferralLink(account);
  const avatarSrc = getRandomAvatar(account);
  const isAutoUser = userDetails?.isAutoUser || false;
  const isNormalUser = userDetails?.isNormalUser || false;
  const pendingReinvest = parseFloat(formatUSDT(userDetails?.pendingReinvest || 0));

  // ---- Investment caps (BigNumber math — no precision loss) ----
  const zero = ethers.BigNumber.from(0);
  const rawMaxAuto = userDetails && planConfig?.maxAutoInvestment
    ? planConfig.maxAutoInvestment
    : zero;
  const rawMaxNormal = userDetails && planConfig?.maxNormalInvestment
    ? planConfig.maxNormalInvestment
    : zero;
  const rawAutoInv = userDetails?.autoInvested || zero;
  const rawNormalInv = userDetails?.normalInvested || zero;

  // Raw remaining as BigNumber
  const rawAutoRemaining = rawMaxAuto.gt(rawAutoInv) ? rawMaxAuto.sub(rawAutoInv) : zero;
  const rawNormalRemaining = rawMaxNormal.gt(rawNormalInv) ? rawMaxNormal.sub(rawNormalInv) : zero;

  // Display-friendly floats (only for UI text)
  const maxAuto = parseFloat(ethers.utils.formatUnits(rawMaxAuto, 18));
  const maxNormal = parseFloat(ethers.utils.formatUnits(rawMaxNormal, 18));
  const autoInvested = parseFloat(ethers.utils.formatUnits(rawAutoInv, 18));
  const normalInvested = parseFloat(ethers.utils.formatUnits(rawNormalInv, 18));
  const autoRemaining = parseFloat(ethers.utils.formatUnits(rawAutoRemaining, 18));
  const normalRemaining = parseFloat(ethers.utils.formatUnits(rawNormalRemaining, 18));
  const normalModeEnabled = planConfig?.normalModeEnabled !== false;

  // Default the modal to the user's primary mode
  useEffect(() => {
    if (isAutoUser) setPurchaseMode('auto');
    else if (isNormalUser) setPurchaseMode('normal');
  }, [isAutoUser, isNormalUser]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [planContract, storageContract, account]);

  const fetchActivitiesFromEvents = async () => {
    if (!planContract || !account || !provider) return [];

    try {
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - 10000);

      const filter = planContract.filters.ActivityLog(account);
      const events = await planContract.queryFilter(filter, fromBlock, currentBlock);
      
      const formatted = events.map((event, idx) => ({
        id: `act-${idx}-${event.args.timestamp}`,
        type: event.args.activityType,
        amount: formatUSDT(event.args.amount),
        timestamp: event.args.timestamp.toString()
      })).reverse();
      
      return formatted;
    } catch (error) {
      console.error('Error fetching activities from events:', error);
      return [];
    }
  };

  const fetchStats = async () => {
    if (!planContract || !storageContract || !account) {
      setLoading(false);
      return;
    }

    try {
      const [
        balance,
        pool,
        teamStats,
        incomeBreakdown,
        claimableIncome
      ] = await Promise.all([
        planContract.getContractBalance(),
        storageContract.capitalPool(),
        storageContract.getUserTeamStats(account),
        storageContract.getUserIncomeBreakdown(account),
        storageContract.getClaimableIncome(account)
      ]);

      setContractBalance(formatUSDT(balance));
      setCapitalPool(formatUSDT(pool));

      setStats({
        totalEarned: formatUSDT(userDetails?.totalEarned || 0),
        teamCount: teamStats.teamCount.toString(),
        directCount: teamStats.directCount.toString(),
        teamBusiness: formatUSDT(teamStats.teamBusiness)
      });

      setIncome({
        sponsorIncome: formatUSDT(incomeBreakdown.sponsorIncome),
        levelIncome: formatUSDT(incomeBreakdown.levelIncome),
        profitIncome: formatUSDT(incomeBreakdown.profitIncome),
        autoReferralIncome: formatUSDT(incomeBreakdown.autoReferralIncome),
        totalEarned: formatUSDT(incomeBreakdown.totalEarned)
      });

      setClaimable({
        sponsor: formatUSDT(claimableIncome.sponsor),
        level: formatUSDT(claimableIncome.level),
        autoRef: formatUSDT(claimableIncome.autoRef),
        total: formatUSDT(claimableIncome.total)
      });

      // Fetch activities from events
      const userActivities = await fetchActivitiesFromEvents();
      setAllActivities(userActivities);
      setActivities(userActivities.slice(0, 5));
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(referralLink);
    if (success) {
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePurchase = async () => {
    const amt = parseFloat(purchaseAmount);
    if (amt < 20) {
      toast.error('Minimum purchase is $20');
      return;
    }

    if (parseFloat(usdtBalance) < amt) {
      toast.error('Insufficient USDT balance');
      return;
    }

    // Enforce cap for the selected mode — compare as BigNumber to match contract
    const isAutoPurchase = purchaseMode === 'auto';
    const rawAmount = parseUSDT(purchaseAmount);
    if (isAutoPurchase && rawAmount.gt(rawAutoRemaining)) {
      toast.error(`Auto cap exceeded. Remaining: $${autoRemaining.toFixed(2)}`);
      return;
    }
    if (!isAutoPurchase && rawAmount.gt(rawNormalRemaining)) {
      toast.error(`Normal cap exceeded. Remaining: $${normalRemaining.toFixed(2)}`);
      return;
    }
    if (!isAutoPurchase && !normalModeEnabled) {
      toast.error('Normal mode is currently disabled');
      return;
    }

    setIsPurchasing(true);
    try {
      const amount = parseUSDT(purchaseAmount);
      const allowance = await usdtContract.allowance(account, planContract.address);

      if (allowance.lt(amount)) {
        toast.info('Approving USDT...');
        const approveTx = await usdtContract.approve(planContract.address, ethers.constants.MaxUint256);
        await approveTx.wait();
        toast.success('USDT approved!');
      }

      toast.info('Processing purchase...');
      // Existing users CAN switch mode per purchase — the contract categorizes
      // the investment by these parameters, not by the stored user flags.
      const tx = await planContract.purchaseNFTs(
        amount,
        ethers.constants.AddressZero,
        isAutoPurchase,
        !isAutoPurchase
      );
      await tx.wait();

      toast.success('Purchase successful!');
      sendActivityNotification('PUR', purchaseAmount);
      setShowPurchaseModal(false);
      await refreshUserData();
      await fetchStats();
    } catch (error) {
      toast.error(error.reason || 'Transaction failed');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRepurchase = async () => {
    if (pendingReinvest <= 0) {
      toast.error('No pending funds to reinvest');
      return;
    }

    // Use custom amount or full pending
    const amtStr = repurchaseAmount.trim();
    let reinvestAmt;

    if (amtStr && parseFloat(amtStr) > 0) {
      const amtFloat = parseFloat(amtStr);
      if (amtFloat > pendingReinvest) {
        toast.error(`Amount exceeds pending balance ($${pendingReinvest.toFixed(2)})`);
        return;
      }
      if (amtFloat < 20) {
        toast.error('Minimum reinvest is $20');
        return;
      }
      reinvestAmt = parseUSDT(amtStr);
    } else {
      // Default: full pending (raw BigNumber to avoid rounding)
      reinvestAmt = userDetails?.pendingReinvest;
    }

    setIsRepurchasing(true);
    try {
      toast.info('Processing repurchase...');
      const tx = await planContract.repurchaseNFTs(reinvestAmt);
      await tx.wait();

      toast.success('Repurchase successful!');
      sendActivityNotification('REP', amtStr || pendingReinvest.toString());
      setRepurchaseAmount('');
      await refreshUserData();
      await fetchStats();
    } catch (error) {
      toast.error(error.reason || 'Transaction failed');
    } finally {
      setIsRepurchasing(false);
    }
  };

  const handleClaim = async () => {
    const totalClaimable = parseFloat(claimable.total);
    
    if (totalClaimable <= 0) {
      toast.error('No income to claim');
      return;
    }

    setIsClaiming(true);
    try {
      toast.info('Processing withdrawal...');
      const tx = await planContract.withdrawIncome();
      await tx.wait();

      toast.success(`Successfully claimed $${formatSmart(totalClaimable)}!`);
      sendActivityNotification('WA', totalClaimable.toString());
      await refreshUserData();
      await fetchStats();
    } catch (error) {
      toast.error(error.reason || 'Withdrawal failed');
    } finally {
      setIsClaiming(false);
    }
  };

  const paginatedActivities = allActivities.slice(
    (activityPage - 1) * ACTIVITIES_PER_PAGE,
    activityPage * ACTIVITIES_PER_PAGE
  );
  const totalPages = Math.ceil(allActivities.length / ACTIVITIES_PER_PAGE);

  if (loading) {
    return <Loader text="Loading Dashboard" />;
  }

  return (
    <div className="home-page">
      {/* Telegram Banner */}
      <div className="telegram-banner animate-fade-up" onClick={() => setShowTelegramModal(true)}>
        <img src="/tele.gif" alt="Telegram" className="tele-gif" />
        <span className="tele-text">Get notified on Telegram</span>
        <span className="tele-arrow">→</span>
      </div>
      <div className="telegram-banner animate-fade-up" onClick={() => {
    window.location.href = "https://t.me/adrenalinenft_official";
  }}>
        <img src="/te.gif" alt="Telegram" className="tele-gif" />
        <span className="tele-text">Join Our Telegram Channel</span>
        <span className="tele-arrow">→</span>
      </div>

      {/* Contract Stats - 1x2 Grid */}
      <div className="contract-stats animate-fade-up delay-1">
        <div className="contract-stat">
          <span className="stat-icon pulse-icon">🏦</span>
          <div className="stat-info">
            <span className="stat-value font-numbers">${formatNumber(parseFloat(contractBalance))}</span>
            <span className="stat-label">Contract Balance</span>
          </div>
        </div>
        <div className="contract-stat">
          <span className="stat-icon pulse-icon">🔄</span>
          <div className="stat-info">
            <span className="stat-value font-numbers">${formatNumber(9989 + parseFloat(capitalPool))}</span>
            <span className="stat-label">Capital Pool</span>
          </div>
        </div>
      </div>

      {/* User Card - ORIGINAL DESIGN */}
      <div className="user-card glass-card animate-fade-up delay-2">
        <div className="user-card-content">
          <div className="user-avatar" style={{ width: '120px', height: '120px' }}>
            <img src={avatarSrc} alt="Avatar" style={{ width: '120px', height: '120px' }} />
            <span className="avatar-ring"></span>
          </div>
          <div className="user-info">
            <div className="user-ids">
              <div className="user-id-box">
                <span className="id-label">Your ID</span>
                <span className="id-value font-mono">{userId}</span>
              </div>
              <div className="user-id-box">
                <span className="id-label">Upline ID</span>
                <span className="id-value font-mono">{uplineId}</span>
              </div>
            </div>
            {/* Mode Box with animation */}
            <div className={`mode-box ${isAutoUser ? 'auto' : 'normal'}`}>
              <span className="mode-icon">{isAutoUser ? '🚀' : '💰'}</span>
              <span className="mode-text">{isAutoUser ? 'Auto Mode' : 'Normal Mode'}</span>
              <div className="mode-glow"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="referral-card glass-card animate-fade-up delay-3">
        <div className="referral-header">
          <span className="referral-label">Your Referral Link</span>
          <span className="referral-bonus pulse-badge">0.25% Bonus</span>
        </div>
        <div className="referral-link-box">
          <input type="text" value={referralLink} readOnly className="referral-input font-mono" />
          <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
            <span className="copy-icon">{copied ? '✓' : '📋'}</span>
          </button>
        </div>
      </div>

      {/* Stats Grid - Original Single Column Layout */}
      <div className="stats-grid animate-fade-up delay-4">
        <div className="stat-card">
          <div className="stat-icon earned pulse-icon">💰</div>
          <div className="stat-content">
            <span className="stat-value font-numbers earned">${formatSmart(parseFloat(stats.totalEarned))}</span>
            <span className="stat-label">Total Earned</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon team pulse-icon">👥</div>
          <div className="stat-content">
            <span className="stat-value font-numbers">{formatNumber(parseInt(stats.teamCount), 0)}</span>
            <span className="stat-label">Total Team</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon direct pulse-icon">🎯</div>
          <div className="stat-content">
            <span className="stat-value font-numbers">{stats.directCount}</span>
            <span className="stat-label">Direct Referrals</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon business pulse-icon">📊</div>
          <div className="stat-content">
            <span className="stat-value font-numbers">${formatNumber(parseFloat(stats.teamBusiness))}</span>
            <span className="stat-label">Team Business</span>
          </div>
        </div>
      </div>

      {/* Investment Cap Progress */}
      <div className="income-section glass-card animate-fade-up">
        <div className="section-header">
          <h3>
            <span className="icon">🎯</span>
            Active Investment
          </h3>
          <span style={{ fontSize: '.65rem', color: 'var(--text-secondary, #888)' }}>
            cap refreshes when principal returns
          </span>
        </div>
        <div className="income-bars">
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">🚀 Auto — Active</span>
              <span className="income-value font-numbers">
                ${formatSmart(autoInvested)} / ${formatNumber(maxAuto)}
              </span>
            </div>
            <div className="income-bar">
              <div
                className="income-fill sponsor"
                style={{ width: `${maxAuto > 0 ? Math.min((autoInvested / maxAuto) * 100, 100) : 0}%` }}
              ></div>
            </div>
          </div>
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">💰 Normal — Active {!normalModeEnabled && '(Disabled)'}</span>
              <span className="income-value font-numbers">
                ${formatSmart(normalInvested)} / ${formatNumber(maxNormal)}
              </span>
            </div>
            <div className="income-bar">
              <div
                className="income-fill level"
                style={{ width: `${maxNormal > 0 ? Math.min((normalInvested / maxNormal) * 100, 100) : 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Claimable Income Section */}
      {parseFloat(claimable.total) > 0 && (
        <div className="claim-section glass-card animate-fade-up">
          <div className="claim-info">
            <div className="claim-icon">💎</div>
            <div className="claim-details">
              <span className="claim-label">Claimable Income</span>
              <span className="claim-value font-numbers">${formatSmart(parseFloat(claimable.total))}</span>
              <div className="claim-breakdown">
                <span className="claim-item">Sponsor: ${formatSmart(parseFloat(claimable.sponsor))}</span>
                <span className="claim-item">Level: ${formatSmart(parseFloat(claimable.level))}</span>
                <span className="claim-item">Auto-Ref: ${formatSmart(parseFloat(claimable.autoRef))}</span>
              </div>
            </div>
          </div>
          <button 
            className="claim-btn"
            onClick={handleClaim}
            disabled={isClaiming}
          >
            {isClaiming ? 'Processing...' : '💳 Claim Now'}
          </button>
        </div>
      )}

      {/* Income Breakdown */}
      <div className="income-section glass-card animate-fade-up delay-5">
        <div className="section-header">
          <h3>
            <span className="icon bounce-icon">📈</span>
            Income Breakdown
          </h3>
          <div className="total-income">
            <span className="total-label">Total</span>
            <span className="total-value font-numbers">${formatSmart(parseFloat(income.totalEarned))}</span>
          </div>
        </div>
        <div className="income-bars">
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">Sponsor Income</span>
              <span className="income-value font-numbers">${formatSmart(parseFloat(income.sponsorIncome))}</span>
            </div>
            <div className="income-bar">
              <div 
                className="income-fill sponsor" 
                style={{ width: `${Math.min((parseFloat(income.sponsorIncome) / Math.max(parseFloat(income.totalEarned), 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">Level Income</span>
              <span className="income-value font-numbers">${formatSmart(parseFloat(income.levelIncome))}</span>
            </div>
            <div className="income-bar">
              <div 
                className="income-fill level" 
                style={{ width: `${Math.min((parseFloat(income.levelIncome) / Math.max(parseFloat(income.totalEarned), 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">NFT Profit</span>
              <span className="income-value font-numbers">${formatSmart(parseFloat(income.profitIncome))}</span>
            </div>
            <div className="income-bar">
              <div
                className="income-fill profit"
                style={{ width: `${Math.min((parseFloat(income.profitIncome) / Math.max(parseFloat(income.totalEarned), 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
          <div className="income-item">
            <div className="income-info">
              <span className="income-label">Auto-Referral Bonus</span>
              <span className="income-value font-numbers">${formatSmart(parseFloat(income.autoReferralIncome))}</span>
            </div>
            <div className="income-bar">
              <div
                className="income-fill sponsor"
                style={{ width: `${Math.min((parseFloat(income.autoReferralIncome) / Math.max(parseFloat(income.totalEarned), 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Buy More Button */}
      <button className="buy-more-btn animate-fade-up delay-6" onClick={() => setShowPurchaseModal(true)}>
        <span className="btn-icon bounce-icon">🛒</span>
        <span className="btn-text">Buy More NFTs</span>
        <span className="btn-glow"></span>
      </button>

      {/* Pending Reinvest Section - Only for Auto users */}
      {isAutoUser && pendingReinvest > 0 && (
        <div className="pending-section glass-card animate-fade-up">
          <div className="pending-info">
            <div className="pending-icon">⏳</div>
            <div className="pending-details">
              <span className="pending-label">Pending Principal</span>
              <span className="pending-value font-numbers">${formatNumber(pendingReinvest)}</span>
            </div>
          </div>

          {/* Custom reinvest amount */}
          <div style={{ margin: '10px 0' }}>
            <div className="purchase-amounts" style={{ marginBottom: '8px' }}>
              {[20, 50, 100].map((amt) => (
                <button
                  key={`rep-${amt}`}
                  className={`amount-btn ${repurchaseAmount === amt.toString() ? 'active' : ''}`}
                  onClick={() => setRepurchaseAmount(amt.toString())}
                  disabled={amt > pendingReinvest}
                  style={{ opacity: amt > pendingReinvest ? 0.4 : 1 }}
                >
                  <span className="font-numbers">${amt}</span>
                </button>
              ))}
              <button
                className={`amount-btn ${repurchaseAmount === '' ? 'active' : ''}`}
                onClick={() => setRepurchaseAmount('')}
              >
                <span className="font-numbers">All</span>
              </button>
            </div>
            <div className="purchase-input" style={{ marginBottom: '8px' }}>
              <input
                type="number"
                className="cyber-input"
                placeholder={`Amount (max $${pendingReinvest.toFixed(2)})`}
                value={repurchaseAmount}
                onChange={(e) => setRepurchaseAmount(e.target.value)}
                min="20"
                max={pendingReinvest}
                style={{ fontSize: '0.85rem' }}
              />
              <span className="input-suffix">USDT</span>
            </div>
          </div>

          <button
            className="repurchase-btn"
            onClick={handleRepurchase}
            disabled={isRepurchasing}
          >
            {isRepurchasing
              ? 'Processing...'
              : repurchaseAmount
                ? `🔄 Reinvest $${repurchaseAmount}`
                : `🔄 Reinvest All $${formatNumber(pendingReinvest)}`}
          </button>
        </div>
      )}

      {/* Recent Activities */}
      <div className="activities-section glass-card animate-fade-up delay-7">
        <div className="section-header">
          <h3>
            <span className="icon spin-icon">⚡</span>
            Recent Activities
          </h3>
        </div>
        {activities.length === 0 ? (
          <div className="no-activities">
            <span>No activities yet</span>
          </div>
        ) : (
          <>
            <div className="activities-list">
              {activities.map((act, idx) => (
                <div key={act.id} className="activity-item" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <span className="activity-type">{getActivityLabel(act.type)}</span>
                  <div className="activity-details">
                    <span className="activity-amount font-numbers">
                      {parseFloat(act.amount) > 0 ? `$${formatSmart(parseFloat(act.amount))}` : '-'}
                    </span>
                    <span className="activity-time">{timeAgo(act.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
            {allActivities.length > 5 && (
              <button className="view-more-btn" onClick={() => setShowActivitiesModal(true)}>
                View All Activities ({allActivities.length})
              </button>
            )}
          </>
        )}
      </div>

      {/* Purchase Modal */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title="Buy NFTs"
        size="small"
      >
        <div className="purchase-modal">
          <div className="purchase-balance">
            <span>Your Balance</span>
            <span className="font-numbers">{parseFloat(usdtBalance).toFixed(2)} USDT</span>
          </div>

          {/* Mode toggle — choose how this specific purchase is categorized */}
          <div className="mode-toggle" style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
            <button
              type="button"
              className={`amount-btn ${purchaseMode === 'auto' ? 'active' : ''}`}
              style={{ flex: 1 }}
              onClick={() => setPurchaseMode('auto')}
            >
              🚀 Auto<br/>
              <span style={{ fontSize: '0.7em', opacity: 0.75 }}>
                Remaining ${autoRemaining.toFixed(2)}
              </span>
            </button>
            <button
              type="button"
              className={`amount-btn ${purchaseMode === 'normal' ? 'active' : ''}`}
              style={{ flex: 1, opacity: normalModeEnabled ? 1 : 0.4 }}
              disabled={!normalModeEnabled}
              onClick={() => normalModeEnabled && setPurchaseMode('normal')}
            >
              💰 Normal<br/>
              <span style={{ fontSize: '0.7em', opacity: 0.75 }}>
                {normalModeEnabled
                  ? `Remaining $${normalRemaining.toFixed(2)}`
                  : 'Disabled'}
              </span>
            </button>
          </div>

          <div className="purchase-amounts">
            {[20, 50, 100, 200, 500, 1000].map((amt) => (
              <button
                key={`purchase-${amt}`}
                className={`amount-btn ${purchaseAmount === amt.toString() ? 'active' : ''}`}
                onClick={() => setPurchaseAmount(amt.toString())}
              >
                <span className="font-numbers">${amt}</span>
              </button>
            ))}
          </div>

          <div className="purchase-input">
            <input
              type="number"
              className="cyber-input"
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e.target.value)}
              min="20"
            />
            <span className="input-suffix">USDT</span>
          </div>

          <button
            className="btn btn-primary btn-purchase"
            onClick={handlePurchase}
            disabled={isPurchasing}
          >
            {isPurchasing ? 'Processing...' : `Purchase ${purchaseMode === 'auto' ? 'Auto' : 'Normal'} $${purchaseAmount}`}
          </button>
        </div>
      </Modal>

      {/* Activities Modal with Pagination */}
      <Modal
        isOpen={showActivitiesModal}
        onClose={() => { setShowActivitiesModal(false); setActivityPage(1); }}
        title="All Activities"
        size="medium"
      >
        <div className="activities-modal">
          <div className="activities-list-full">
            {paginatedActivities.map((act) => (
              <div key={act.id} className="activity-item">
                <span className="activity-type">{getActivityLabel(act.type)}</span>
                <div className="activity-details">
                  <span className="activity-amount font-numbers">
                    {parseFloat(act.amount) > 0 ? `$${formatSmart(parseFloat(act.amount))}` : '-'}
                  </span>
                  <span className="activity-time">{timeAgo(act.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                disabled={activityPage === 1}
              >
                ←
              </button>
              <span className="page-info font-numbers">{activityPage} / {totalPages}</span>
              <button 
                className="page-btn" 
                onClick={() => setActivityPage(p => Math.min(totalPages, p + 1))}
                disabled={activityPage === totalPages}
              >
                →
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Telegram Modal */}
      {showTelegramModal && (
        <TelegramNotify 
          account={account}
          userDetails={userDetails}
          onClose={() => setShowTelegramModal(false)}
        />
      )}
    </div>
  );
};

export default Home;